import type {
  AppointmentType as AppointmentTypeT,
  CreateAppointmentProfileRequest,
  LocationModel,
  ReasonCode,
} from '@/types/scheduling-api';
import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * Build the form-state subset that represents "an appointment profile
 * has been picked" — matches what
 * `appointment-profile-section.tsx#selectProfile` synchronously
 * sets when the user clicks a profile card.
 *
 * Used by:
 *   - The section itself when the user picks via the in-panel UI.
 *   - `frame-scheduling.tsx#handleQuickCreate` (PROD-829) when
 *     quick-create from the provider strip pre-fills the panel.
 *
 * Async fields populated by the `usePreBookAppointmentProfile`
 * mutation (`reminderProfile*`, `billingProfile*`,
 * `reasonCodeOptions`) are NOT covered here — they land after the
 * mutation resolves; the appointment-profile section runs that on
 * its own.
 *
 * PascalCase tolerance: some endpoints ship `Code`/`Display`/`Definition`
 * for nested fields. Same coerce logic as `selectProfile` so both
 * paths agree.
 */
export function buildProfileFormValues(
  profile: CreateAppointmentProfileRequest,
): Partial<AppointmentFormValues> {
  const at = profile.appointmentType as
    | (AppointmentTypeT & { Code?: string; Display?: string; Definition?: string })
    | undefined;
  const atCode = at?.code ?? at?.Code;
  const atDisplay = at?.display ?? at?.Display;
  const appointmentType: AppointmentTypeT | null =
    atCode && atDisplay
      ? {
          code: atCode,
          display: atDisplay,
          definition: at?.definition ?? at?.Definition ?? '',
        }
      : null;

  const rc = profile.reasonCode as
    | (ReasonCode & { Code?: string; Display?: string; Definition?: string })
    | undefined;
  const rcCode = rc?.code ?? rc?.Code;
  const rcDisplay = rc?.display ?? rc?.Display;
  const reasonCode: ReasonCode | null =
    rcCode && rcDisplay
      ? {
          code: rcCode,
          display: rcDisplay,
          definition: rc?.definition ?? rc?.Definition ?? '',
        }
      : null;

  return {
    appointmentProfileId: profile.id ?? null,
    appointmentProfileName: profile.profileName,
    appointmentProfileColor: profile.color ?? null,
    pickedProfile: profile,
    appointmentType,
    reasonCode,
    minutesDuration: profile.minutesDuration ?? null,
    // Location drives the location-picker landing + the per-location
    // profile filter at the section level. Coerce undefined → null
    // for AppointmentFormValues compatibility.
    location: (profile.location as LocationModel | undefined) ?? null,
  };
}
